import { useEffect, useImperativeHandle, forwardRef } from 'react';
import { useCuppingProfileForm } from '../../../hooks/inventory-hooks/useCuppingProfileForm';
import type { CuppingProfile } from '../../../services/profileService';
import type { CuppingSessionDetail } from '../../../types/cuppingSession';
import { CuppingFormSections, CuppingMetaFields } from '../../inventory/components/CuppingFormSections';

export interface CuppingSessionItemFormHandle {
  submit: () => Promise<void>;
}

interface CuppingSessionItemFormProps {
  detail: CuppingSessionDetail;
  sessionId: string;
  profile?: CuppingProfile;
  readOnly: boolean;
  blindMode?: boolean;
  onSaved?: () => void;
  onDirtyChange?: (inventoryItemId: string, isDirty: boolean) => void;
  topTabs?: React.ReactNode;
  bottomTabs?: React.ReactNode;
  sessionMode: 'Simplified' | 'Full' | 'SCA';
  isActive?: boolean;
  onWorkOrderClick?: (workOrderId: string) => void;
  /** When false, sections are not lock-gated — every score input is immediately
   *  editable (used during the onboarding practice cupping so the user can drag
   *  a slider straight away instead of first clicking "Unlock Section"). */
  lockSections?: boolean;
}

export const CuppingSessionItemForm = forwardRef<CuppingSessionItemFormHandle, CuppingSessionItemFormProps>(
  (
    {
      detail,
      sessionId,
      profile,
      readOnly,
      blindMode,
      onSaved,
      onDirtyChange,
      topTabs,
      bottomTabs,
      sessionMode,
      isActive,
      onWorkOrderClick,
      lockSections = true,
    },
    ref,
  ) => {
    const {
      register,
      control,
      handleSubmit,
      watch,
      setValue,
      getValues,
      mode: localMode,
      setMode: setLocalMode,
      maxScore,
      scaComputedFinalScore,
      handleSave,
      formState,
    } = useCuppingProfileForm({
      initialData: profile,
      entityType: 'ItemType',
      entityId: detail.inventoryItemId,
      sessionId,
      onSuccess: onSaved,
    });

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          await handleSubmit(handleSave)();
        },
      }),
      [handleSubmit, handleSave],
    );

    useEffect(() => {
      onDirtyChange?.(detail.inventoryItemId, formState.isDirty);
    }, [detail.inventoryItemId, formState.isDirty, onDirtyChange]);

    useEffect(() => {
      if (localMode !== sessionMode) {
        setLocalMode(sessionMode);
      }
    }, [localMode, sessionMode, setLocalMode]);

    return (
      <form
        onSubmit={handleSubmit(handleSave)}
        className="w-full bg-white rounded-lg shadow-sm p-4 overflow-hidden"
        data-test-id="cupping-session-form"
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <div className="px-3 py-1.5 text-xs font-bold rounded-md bg-gray-100 text-gray-900 shadow-sm border border-gray-200 uppercase tracking-wide">
            {sessionMode === 'Full' ? 'Descriptive Form' : sessionMode === 'SCA' ? 'Affective Form' : 'Simplified'}
          </div>
        </div>
        {!blindMode && (
          <div
            data-test-id="cupping-session-active-wo"
            onClick={() => detail.workOrderId && onWorkOrderClick?.(detail.workOrderId)}
            className="mb-6 -mt-2 p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer shadow-sm transition-colors"
          >
            <div className="font-bold text-gray-900">{detail.workOrderNumber}</div>
            <div className="text-gray-700">{detail.itemName}</div>
            <div className="text-sm text-gray-500 uppercase">{detail.roastType} roast</div>
          </div>
        )}

        {topTabs ? (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6 pb-4 border-b border-gray-100">
              <div className="flex-1 min-w-0">
                <div className="overflow-x-auto">{topTabs}</div>
              </div>
              <div className="shrink-0">
                <CuppingMetaFields register={register} readOnly={readOnly} />
              </div>
            </div>
          </>
        ) : (
          <div className="flex justify-end mb-6 pb-4 border-b border-gray-100">
            <CuppingMetaFields register={register} readOnly={readOnly} />
          </div>
        )}

        <CuppingFormSections
          key={`${detail.inventoryItemId}-${isActive ? 'active' : 'inactive'}`}
          control={control}
          register={register}
          setValue={setValue}
          getValues={getValues}
          watch={watch}
          maxScore={maxScore}
          mode={localMode}
          scaScore={scaComputedFinalScore}
          readOnly={readOnly}
          enableSectionLocks={lockSections}
          defaultSectionsLocked={lockSections}
          isActive={isActive}
        />
        {bottomTabs && (
          <div className="mt-6">
            <div className="overflow-x-auto">
              {bottomTabs}
            </div>
          </div>
        )}
      </form>
    );
  },
);

CuppingSessionItemForm.displayName = 'CuppingSessionItemForm';
